/**
 * Enhanced Tab State Cache Manager
 * Preserves DOM state of tab sections after dynamic updates from API calls
 */

class TabStateCache {
  constructor() {
    this.cache = new Map();
    this.metadata = new Map();
    this.maxCacheSize = 100; // Prevent memory bloat
  }

  /**
   * Generate unique cache key for lead and document type
   * @param {string} leadId - Lead identifier
   * @param {string} docType - Document type identifier
   * @returns {string} Unique cache key
   */
  generateKey(leadId, docType) {
    return `${leadId}_${docType}`.replace(/\s+/g, '_');
  }

  /**
   * Store DOM state with metadata
   * @param {string} key - Cache key
   * @param {string} htmlContent - HTML content to cache
   * @param {object} metadata - Additional metadata
   */
  store(key, htmlContent, metadata = {}) {
    // Implement LRU-style cleanup if cache gets too large
    if (this.cache.size >= this.maxCacheSize) {
      this.cleanup();
    }

    this.cache.set(key, htmlContent);
    this.metadata.set(key, {
      timestamp: Date.now(),
      version: metadata.version || 1,
      docType: metadata.docType || 'unknown',
      ...metadata
    });

  }

  /**
   * Retrieve cached content
   * @param {string} key - Cache key
   * @returns {string|null} Cached HTML content or null
   */
  get(key) {
    const content = this.cache.get(key);
    if (content) {
      // console.log(`[TabStateCache] Retrieved content for key: ${key}`);
      // Update access time for LRU
      const meta = this.metadata.get(key);
      if (meta) {
        meta.lastAccessed = Date.now();
        this.metadata.set(key, meta);
      }
    }
    return content || null;
  }

  /**
   * Check if content exists in cache
   * @param {string} key - Cache key
   * @returns {boolean} True if content exists
   */
  has(key) {
    return this.cache.has(key);
  }

  /**
   * Get metadata for cached content
   * @param {string} key - Cache key
   * @returns {object|null} Metadata object or null
   */
  getMetadata(key) {
    return this.metadata.get(key) || null;
  }

  /**
   * Clear cache for specific lead
   * @param {string} leadId - Lead identifier
   */
  clearLead(leadId) {
    const keysToDelete = [];
    const prefix = `${leadId}_`;
    
    for (const key of this.cache.keys()) {
      if (key.startsWith(prefix)) {
        keysToDelete.push(key);
      }
    }
    
    keysToDelete.forEach(key => {
      this.cache.delete(key);
      this.metadata.delete(key);
    });

    console.log(`[TabStateCache] Cleared ${keysToDelete.length} entries for lead: ${leadId}`);
  }

  /**
   * Clear specific document cache
   * @param {string} leadId - Lead identifier
   * @param {string} docType - Document type identifier
   */
  clearDocument(leadId, docType) {
    const key = this.generateKey(leadId, docType);
    const deleted = this.cache.delete(key);
    this.metadata.delete(key);
    
    if (deleted) {
      console.log(`[TabStateCache] Cleared document cache for key: ${key}`);
    }
  }

  /**
   * Clear all cache entries
   */
  clearAll() {
    const size = this.cache.size;
    this.cache.clear();
    this.metadata.clear();
    console.log(`[TabStateCache] Cleared all ${size} cache entries`);
  }

  /**
   * Cleanup old cache entries (LRU-style)
   */
  cleanup() {
    const entries = Array.from(this.metadata.entries());
    
    // Sort by last accessed time (oldest first)
    entries.sort((a, b) => {
      const aTime = a[1].lastAccessed || a[1].timestamp;
      const bTime = b[1].lastAccessed || b[1].timestamp;
      return aTime - bTime;
    });

    // Remove oldest 20% of entries
    const toRemove = Math.floor(entries.length * 0.2);
    for (let i = 0; i < toRemove; i++) {
      const key = entries[i][0];
      this.cache.delete(key);
      this.metadata.delete(key);
    }

    console.log(`[TabStateCache] Cleaned up ${toRemove} old cache entries`);
  }

  /**
   * Get cache statistics
   * @returns {object} Cache statistics
   */
  getStats() {
    return {
      size: this.cache.size,
      maxSize: this.maxCacheSize,
      keys: Array.from(this.cache.keys()),
      memoryUsage: this.estimateMemoryUsage()
    };
  }

  /**
   * Estimate memory usage (rough calculation)
   * @returns {number} Estimated memory usage in bytes
   */
  estimateMemoryUsage() {
    let totalSize = 0;
    for (const [key, content] of this.cache.entries()) {
      totalSize += key.length * 2; // UTF-16 characters
      totalSize += content.length * 2;
    }
    return totalSize;
  }

  /**
   * Check if cache entry is stale
   * @param {string} key - Cache key
   * @param {number} maxAge - Maximum age in milliseconds (default: 1 hour)
   * @returns {boolean} True if entry is stale
   */
  isStale(key, maxAge = 14400000) {
    const meta = this.metadata.get(key);
    if (!meta) return true;
    
    return (Date.now() - meta.timestamp) > maxAge;
  }
}

// Create singleton instance
export const tabStateCache = new TabStateCache();

// Export class for testing
export { TabStateCache };
